import { NotificationData, SeatApiData, MemberApiData } from '../models/OwnerLayout';
import { getTodayKST } from '../../../utils/dateUtils';

/**
 * 오너 레이아웃 서비스
 */
export class OwnerLayoutService {
  /**
   * 내부망에서 오너 경로 생성
   */
  getOwnerPath(path: string): string {
    // 현재 URL에 /studyroom이 포함되어 있으면 prefix 유지
    const pathname = window.location.pathname;
    if (pathname.startsWith('/studyroom')) {
      return `/studyroom${path}`;
    }
    return path;
  }

  /**
   * 좌석 데이터 기반 알림 계산
   */
  calculateSeatAlerts(seatData: SeatApiData): {
    alerts: number;
    checkIns: number;
  } {
    const outOfOrderSeats =
      seatData.seats?.filter((s) => s.is_out_of_order).length || 0;
    const occupiedSeats =
      seatData.seats?.filter((s) => !s.is_available && !s.is_out_of_order)
        .length || 0;
    const occupancyRate = seatData.statistics?.occupancyRate || 0;

    // 긴급 알림: 고장 좌석이 있거나 점유율이 90% 이상일 때
    const alerts =
      outOfOrderSeats > 0 ? outOfOrderSeats : occupancyRate > 90 ? 1 : 0;

    // 최대 5개까지만 표시
    const checkIns = occupiedSeats > 0 ? Math.min(occupiedSeats, 5) : 0;

    return { alerts, checkIns };
  }

  /**
   * 회원 데이터 기반 신규 회원 계산
   */
  calculateNewMembers(memberData: MemberApiData): number {
    const today = getTodayKST();
    return (
      memberData.members?.filter(
        (m) => m.createdAt && m.createdAt.startsWith(today)
      ).length || 0
    );
  }

  /**
   * 전체 알림 수 계산
   */
  getTotalNotifications(notifications: NotificationData): number {
    return (
      notifications.alerts + notifications.newMembers + notifications.checkIns
    );
  }

  /**
   * localStorage에서 조직명 추출 (캐시 우선)
   */
  getOrganizationName(): string {
    // 1순위: 캐시된 조직 정보
    try {
      const cached = localStorage.getItem('cached_organization_info');
      if (cached) {
        const orgInfo = JSON.parse(cached);
        if (orgInfo.name) {
          return orgInfo.name;
        }
      }
    } catch (e) {
      console.error('캐시된 조직 정보 조회 실패:', e);
    }

    // 2순위: user-storage의 currentUser
    try {
      const userStorage = localStorage.getItem('user-storage');
      if (userStorage) {
        const parsed = JSON.parse(userStorage);
        if (parsed.state?.currentUser?.organization_name) {
          return parsed.state.currentUser.organization_name;
        }
      }
    } catch (e) {
      console.error('user-storage 조회 실패:', e);
    }

    // 3순위: organization localStorage
    const orgFromStorage = localStorage.getItem('organization')?.split(' ')[0];
    if (orgFromStorage) {
      return orgFromStorage;
    }

    // 4순위: organization_id가 있으면 비동기로 조회 시작 (임시로 로딩 표시)
    const orgId = localStorage.getItem('organization_id');
    if (orgId) {
      // 비동기로 조직 정보 조회 (한 번만)
      this.fetchAndCacheOrganizationName(orgId);
      return '로딩중...';
    }

    // 최종 폴백
    return 'OO독서실';
  }

  /**
   * organization_id로 조직 이름 조회 후 캐시
   */
  private async fetchAndCacheOrganizationName(orgId: string): Promise<void> {
    // 이미 조회 중이면 스킵
    if (localStorage.getItem('org_name_fetching') === 'true') {
      return;
    }

    try {
      localStorage.setItem('org_name_fetching', 'true');
      const token = localStorage.getItem('accessToken');
      if (!token) return;

      const response = await fetch(`/api/v1/owner/organization/${orgId}/name`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.name) {
          localStorage.setItem('cached_organization_info', JSON.stringify({
            name: data.name,
            id: orgId
          }));
          // 페이지 새로고침하여 반영
          window.location.reload();
        }
      }
    } catch (e) {
      console.error('조직 이름 조회 실패:', e);
    } finally {
      localStorage.removeItem('org_name_fetching');
    }
  }

  /**
   * 로그아웃 처리
   */
  handleLogout(): void {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user_type');
    localStorage.removeItem('organization');
  }

  /**
   * 사이드바 상태 저장
   */
  saveSidebarState(collapsed: boolean): void {
    localStorage.setItem('ownerSidebarCollapsed', collapsed.toString());
  }

  /**
   * 사이드바 상태 불러오기
   */
  loadSidebarState(): boolean {
    const saved = localStorage.getItem('ownerSidebarCollapsed');
    return saved === 'true';
  }
}

export const ownerLayoutService = new OwnerLayoutService();
